import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import "../styles/cookie-consent.css"; // adapte le chemin si besoin

// Store Zustand pour gérer le consentement cookies
const useCookieConsent = create(
  persist(
    (set) => ({
      status: "unknown", // "unknown" | "accepted" | "rejected" | "custom"
      preferences: {
        necessary: true,   // toujours vrai
        analytics: false,
        marketing: false,
      },
      setStatus: (value) => set({ status: value }),
      setPreferences: (prefs) =>
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        })),
    }),
    {
      name: "cookie-consent", // clé dans localStorage
    }
  )
);

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const { status, preferences, setStatus, setPreferences } = useCookieConsent();

  // Afficher le popup seulement si aucun choix n'a été fait
  useEffect(() => {
    if (status === "unknown") {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // Si déjà choisi ou pas visible → rien à afficher
  if (!isVisible || status !== "unknown") return null;

  const handleAccept = () => {
    setPreferences({
      analytics: true,
      marketing: true,
    });
    setStatus("accepted");
    setIsVisible(false);
    // Ici tu peux déclencher l'init de tes scripts analytics / pixels
  };

  const handleReject = () => {
    setPreferences({
      analytics: false,
      marketing: false,
    });
    setStatus("rejected");
    setIsVisible(false);
    // Ici tu veilles à ne pas charger les scripts non essentiels
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleToggle = (key) => {
    setPreferences({ [key]: !preferences[key] });
  };

  const handleSavePreferences = () => {
    setStatus("custom");
    setIsVisible(false);
    // Ici tu peux charger uniquement les scripts autorisés
  };

  return (
    <div className="cookie-overlay">
      <div className="cookie-popup">
        <div className="cookie-header">
          <h3>Gestion des cookies</h3>
          <button
            className="close-button"
            onClick={() => setIsVisible(false)}
            aria-label="Fermer le bandeau de cookies"
          >
            <X />
          </button>
        </div>

        {!isSettingsOpen && (
          <>
            <p>
              Nous utilisons des cookies pour améliorer votre expérience,
              mesurer l’audience et personnaliser certains contenus. Vous pouvez
              accepter, refuser ou personnaliser vos choix. Vous pourrez à tout
              moment modifier votre décision depuis notre page{" "}
              <a href="/privacy">Politique de confidentialité &amp; RGPD</a>.
            </p>

            <div className="cookie-actions">
              <button
                type="button"
                className="reject-button"
                onClick={handleReject}
              >
                Tout refuser
              </button>

              <button
                type="button"
                className="settings-button"
                onClick={handleOpenSettings}
              >
                Personnaliser
              </button>

              <button
                type="button"
                className="accept-button"
                onClick={handleAccept}
              >
                Tout accepter
              </button>
            </div>

            <div className="cookie-links">
              En savoir plus dans notre{" "}
              <a href="/privacy">Politique de confidentialité &amp; RGPD</a>.
            </div>
          </>
        )}

        {isSettingsOpen && (
          <div className="cookie-settings">
            <p>
              Choisissez les catégories de cookies que vous acceptez. Les
              cookies techniques sont nécessaires au fonctionnement du site et
              ne peuvent pas être désactivés.
            </p>

            <div className="cookie-option">
              <div className="cookie-option-main">
                <span className="cookie-option-title">
                  Cookies techniques (obligatoires)
                </span>
                <span className="cookie-option-badge">Toujours actifs</span>
              </div>
              <p className="cookie-option-desc">
                Indispensables au bon fonctionnement du site, à la sécurité et
                à la sauvegarde de vos choix de confidentialité.
              </p>
            </div>

            <div className="cookie-option">
              <div className="cookie-option-main">
                <span className="cookie-option-title">
                  Cookies statistiques
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handleToggle("analytics")}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
              <p className="cookie-option-desc">
                Permettent de mesurer l’audience (pages visitées, temps passé,
                etc.) pour améliorer le site.
              </p>
            </div>

            <div className="cookie-option">
              <div className="cookie-option-main">
                <span className="cookie-option-title">
                  Cookies marketing &amp; réseaux sociaux
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handleToggle("marketing")}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
              <p className="cookie-option-desc">
                Utilisés pour personnaliser les contenus, les publicités ou
                intégrer des services tiers (ex. : vidéos, partage réseaux
                sociaux).
              </p>
            </div>

            <div className="cookie-settings-actions">
              <button
                type="button"
                className="reject-button"
                onClick={handleCloseSettings}
              >
                Annuler
              </button>
              <button
                type="button"
                className="accept-button"
                onClick={handleSavePreferences}
              >
                Enregistrer mes choix
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
